import { useId } from 'react'
import { Props_SnippetContentColumn } from './ContentColumn.props'
import { Legacy_CompileMarkdown } from '../../../../Shared/AttraxTs/React/ReactSnippetMDCompiler'
import SnippetLink from '../Shared/Link/Link'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, Pagination, A11y } from 'swiper/modules'
import { SwiperNavigation, SwiperPagination, swiperPaginationOptions } from '../Shared/SwiperControls/SwiperControls'

const SnippetContentColumn = (props: Props_SnippetContentColumn) => {
    const { heading, repeatable_entry, background } = props
    const classHeadingVisibility: string = heading.visibility.value ? 'snippet-content-column__heading--visible' : 'snippet-content-column__heading--hidden'

    const backgroundMap = {
        'white': '#ffffff',
        'covea-dark-blue': '#122147',
        'covea-light-green': '#d6eac1',
        'covea-light-red': '#efded3',
        'covea-light-purple': '#e2d3f2',
        'covea-light-turquoise': '#c8eaed',
        'covea-light-yellow': '#eae8ad',
        'MAAF-dark-blue': '#0091a7',
        'MAAF-light-blue': '#e5f3f5',
        'MAAF-light-red': '#fef6f2',
        'MMA-light-green': '#d6eceb',
        'MMA-light-orange': '#f2e1b5',
        'MMA-dark-blue': '#1d3b6d',
        'GMF-dark-blue': '#144390',
        'GMF-light-blue': '#dfedff',
        'GMF-light-orange': '#fff4ed',
        'Fidelia-dark-green': '#269b38',
        'Fidelia-light-blue': '#e1f0fb',
        'Fidelia-light-green': '#ebf2e7'
    }

    const backgroundKey = background?.colour.value || 'white'
    const backgroundValue = backgroundMap[backgroundKey as keyof typeof backgroundMap]
    const darkBackgrounds = ['covea-dark-blue', 'MAAF-dark-blue', 'MMA-dark-blue', 'GMF-dark-blue', 'Fidelia-dark-green']
    const useWhiteText = darkBackgrounds.includes(backgroundKey) ? 'snippet-content-column--white-text' : ''

    const sanitizedId = useId().replace(/:/g, '')
    const carouselId = `content-column-carousel-${sanitizedId}`
    const carouselHeadingId = `${carouselId}-heading`
    const showNavigation = repeatable_entry.length >= 4
    
    return (
        <section className={`snippet-content-column ${useWhiteText}`} style={{ backgroundColor: backgroundValue }}>
            <div className={`snippet-content-column__container`}>
                <h2 id={carouselHeadingId} className={`snippet-content-column__heading ${classHeadingVisibility}`}>{heading.content.value}</h2>
                <div className="snippet-content-column__content-container">
                    <Swiper
                        className="snippet-content-column__container-entries"
                        modules={[Navigation, Pagination, A11y]}
                        a11y={{
                            enabled: true,
                            prevSlideMessage: `{{js.swiper.carousel.navigation.button.previous.aria-label}}`,
                            nextSlideMessage: `{{js.swiper.carousel.navigation.button.next.aria-label}}`,
                            wrapperLiveRegion: false,
                        }}
                        navigation={{
                            prevEl: `.swiper-navigation__button-prev-${carouselId}`,
                            nextEl: `.swiper-navigation__button-next-${carouselId}`,
                        }}
                        pagination={{
                            ...swiperPaginationOptions,
                            el: `.swiper-pagination-${carouselId}`,
                        }}
                        slidesPerView={1}
                        spaceBetween={15}
                        centerInsufficientSlides={true}
                        loop={true}
                        breakpoints={{
                            666: {
                                slidesPerView: 2,
                            },
                            991: {
                                slidesPerView: 4,
                                loop: true
                            },
                        }}
                    >
                        {repeatable_entry.map(({ image, alt, heading, content, linkHref, linkStyle, linkText, linkTarget }, index: number) => (
                            <SwiperSlide key={index} className={`snippet-content-column__entry`}>
                                {image?.value && (
                                    <figure className="snippet-content-column__entry-image-container">
                                        <img
                                            className="snippet-content-column__entry-image"
                                            loading="lazy"
                                            height={190}
                                            width={350}
                                            src={image.value}
                                            alt={alt?.value ? alt.value : ''}
                                        />
                                    </figure>
                                )}
                                <div className="snippet-content-column__entry-content-container">
                                    {heading?.value && (
                                        <h3 className="snippet-content-column__entry-title">{heading.value}</h3>
                                    )}
                                    {content?.value && <div className="snippet-content-column__entry-content">{Legacy_CompileMarkdown(content.value)}</div>}
                                    <SnippetLink
                                        linkStyles={linkStyle?.value}
                                        className="snippet-content-column__entry-link"
                                        url={linkHref?.value}
                                        openInNewTab={linkTarget?.value}
                                    >
                                        {linkText?.value}
                                    </SnippetLink>
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                    {showNavigation && <SwiperNavigation uniqueId={carouselId} className="snippet-content-column__navigation" headingId={carouselHeadingId} />}
                    <SwiperPagination uniqueId={carouselId} className="snippet-content-column__pagination" />
                </div>
                
            </div>
        </section>
    )
}

export default SnippetContentColumn